import React, { useEffect, useRef, useState } from "react";
import { Route, Routes, useLocation, Navigate } from "react-router-dom";

import Home from "../pages/Home";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Backdrops from "../pages/Backdrops";
import ContactUs from "../pages/ContactUs";

// ─── New pages ────────────────────────────────────────────────────────────────
import BookingPage from "../pages/BookingPage";
import LoginPage from "../pages/LoginPage";
import AdminLoginPage from "../pages/AdminLoginPage";
import SignupPage from "../pages/SignupPage";
import VerifyOTP from "../pages/VerifyOTP";
import ForgotPassword from "../pages/ForgotPassword";
import DashboardLayout from "./DashboardLayout";
import AdminLayout from "./AdminLayout";
import OrganizerLayout from "../organizer/OrganizerLayout";

import { useAuth } from "../context/AuthContext";

// ─── Route guards ─────────────────────────────────────────────────────────────
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ background: '#0B0F1F', height: '100vh' }} />;
  return user ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ background: '#0B0F1F', height: '100vh' }} />;
  if (!user) return <Navigate to="/admin-login" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
}

function OrganizerRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ background: '#0B0F1F', height: '100vh' }} />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'event_organizer' && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Extract all background-image URLs from a container
function collectBackgroundUrls(rootEl) {
  if (!rootEl) return [];
  const els = Array.from(rootEl.querySelectorAll("*"));
  const urls = new Set();
  const regex = /url\((?:'|")?([^"')]+)(?:'|")?\)/g;
  els.forEach((el) => {
    const style = window.getComputedStyle(el);
    const bg = style.backgroundImage;
    if (!bg || bg === "none") return;
    let match;
    while ((match = regex.exec(bg)) !== null) {
      if (match[1]) urls.add(match[1]);
    }
  });
  return Array.from(urls);
}

function RootLayout() {
  const [loading, setLoading] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const contentRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const container = contentRef.current || document.body;
      const imgs = Array.from(container.querySelectorAll("img"));
      const bgUrls = collectBackgroundUrls(container);
      const total = imgs.length + bgUrls.length;

      if (total === 0) {
        setFadeOut(true);
        const to = setTimeout(() => setLoading(false), 300);
        return () => clearTimeout(to);
      }

      let fallback;
      let loaded = 0;
      const mark = () => {
        loaded += 1;
        if (loaded >= total) {
          setFadeOut(true);
          const to = setTimeout(() => setLoading(false), 300);
          cleanup();
          if (fallback) clearTimeout(fallback);
          return () => clearTimeout(to);
        }
      };
      const listeners = [];
      imgs.forEach((img) => {
        if (img.complete && img.naturalWidth > 0) { mark(); }
        else {
          const onLoad = () => mark();
          const onError = () => mark();
          img.addEventListener("load", onLoad);
          img.addEventListener("error", onError);
          listeners.push(() => {
            img.removeEventListener("load", onLoad);
            img.removeEventListener("error", onError);
          });
        }
      });
      bgUrls.forEach((url) => {
        const probe = new Image();
        probe.onload = () => mark();
        probe.onerror = () => mark();
        probe.decoding = "async";
        probe.loading = "eager";
        probe.src = url;
      });
      fallback = setTimeout(() => {
        setFadeOut(true);
        const to = setTimeout(() => setLoading(false), 300);
        cleanup();
        return () => clearTimeout(to);
      }, 10000);
      function cleanup() { listeners.forEach((off) => off()); }
      return () => { cleanup(); clearTimeout(fallback); };
    });
    return () => cancelAnimationFrame(raf);
  }, [pathname]);

  // ─── Full-page routes (no public Navbar/Footer) ───────────────────────────
  const fullPagePaths = ['/dashboard', '/admin', '/organizer', '/book', '/login', '/admin-login', '/signup', '/verify', '/forgot-password'];
  const isFullPage = fullPagePaths.some(p => pathname.startsWith(p));

  if (isFullPage) {
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route path="/book" element={<BookingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/admin-login" element={<AdminLoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/verify" element={<VerifyOTP />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/dashboard/*" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>} />
          <Route path="/admin/*" element={<AdminRoute><AdminLayout /></AdminRoute>} />
          <Route path="/organizer/*" element={<OrganizerRoute><OrganizerLayout /></OrganizerRoute>} />
        </Routes>
      </>
    );
  }

  // ─── Public routes (with Navbar + Footer) ─────────────────────────────────
  return (
    <>
      <ScrollToTop />
      <div ref={contentRef} className={`${loading ? "overflow-hidden" : ""}`}>
        <Navbar />
        <div className="w-full">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/backdrops" element={<Backdrops />} />
            <Route path="/contact" element={<ContactUs />} />
          </Routes>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default RootLayout;